import type { IIncentiveRequestApiResponse } from "@/types/incentiveRequestApiTypes";
import type { IIncentiveRequest, RequestStatus } from "@/types/requestTypes";

/**
 * Maps backend RequestStatusId (numeric) to frontend RequestStatus union.
 *
 * Backend constants:
 *   1 = SelfApproved
 *   2 = PendingManagerApproval
 *   3 = Approved
 *   4 = Rejected
 *   5 = Generating
 */
const API_STATUS_MAP: Record<string, RequestStatus> = {
  "Self-approved": "SelfApproved",
  "Pending manager approval": "PendingManagerApproval",
  "Approved": "Approved",
  "Rejected": "Rejected",
  "Generating": "Generating",
};

export function mapApiStatusToRequestStatus(apiStatus: string): RequestStatus {
  const mapped = API_STATUS_MAP[apiStatus];

  if (!mapped) {
    console.warn(`Unknown API status "${apiStatus}", defaulting to "PendingManagerApproval"`);
    return "PendingManagerApproval";
  }

  return mapped;
}

/**
 * Formats an ISO 8601 date string to "MM/DD/YYYY HH:mm" for display.
 */
export function formatRequestDate(isoDate: string): string {
  const date = new Date(isoDate);

  if (isNaN(date.getTime())) {
    return isoDate;
  }

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${month}/${day}/${year} ${hours}:${minutes}`;
}

/**
 * Builds the statusDetail string from modifier info when available.
 */
function buildStatusDetail(response: IIncentiveRequestApiResponse): string | undefined {
  if (response.modifiedBy && response.modifiedDate && response.modifiedBy !== response.createdBy) {
    return `${response.modifiedByName ?? "Unknown"} ${formatRequestDate(response.modifiedDate)}`;
  }

  return undefined;
}

/**
 * Transforms a single API response DTO into the frontend domain model.
 */
export function mapApiResponseToIncentiveRequest(
  response: IIncentiveRequestApiResponse,
): IIncentiveRequest {
  return {
    id: response.id,
    partnerOneId: response.partnerOneId,
    partnerName: response.partnerName,
    requestedOn: formatRequestDate(response.createdDate),
    status: mapApiStatusToRequestStatus(response.requestStatusDisplayName),
    statusDetail: buildStatusDetail(response),
    workbookPassword: response.workbookPassword ?? undefined,
    downloadUrl: response.documentUrl ?? undefined,
  };
}

/**
 * Transforms a collection of API responses into frontend domain models.
 */
export function mapApiResponsesToIncentiveRequests(
  responses: IIncentiveRequestApiResponse[],
): IIncentiveRequest[] {
  return responses.map(mapApiResponseToIncentiveRequest);
}
